import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useTransition,
} from "react";
import { MessageDescriptor } from "@lingui/core";
import { ArrowLeft } from "src/organisms/MobileFilters/DrawerFilter.styled";
import {
  MobileClearAllButton,
  MobileFilterContainer,
} from "./MobileFilter.styled";
import { LabelNavigation } from "../LabelNavigation";
import classNames from "classnames";
import { Flex } from "@otrium/core";
import { Price } from "src/atoms/Price";
import { Text } from "@otrium/atoms";
import { Button } from "src/atoms/Button";
import { Trans } from "@lingui/macro";
import { Range } from "rc-slider";
import { theme } from "src/theme";
import { useFacetContext } from "src/organisms/ProductList/components/Facets/facet.context";
import { useListingState } from "src/hooks/stores/use-listing-store";
import type { ListingFacet } from "src/types/adapters/listing.type";

type RangeBoundaries = [number, number];

type MobileRangeFilterProps = {
  title: MessageDescriptor;
  isOpen: boolean;
  facet: ListingFacet;
  toggleOpen: () => void;
};

function MobileRangeFilter({
  facet,
  isOpen,
  title,
  toggleOpen,
}: MobileRangeFilterProps): JSX.Element | null {
  const { setRange, clearKey } = useFacetContext();
  const { totalResults: total } = useListingState();
  const [_, startTransition] = useTransition();

  const bounds = useMemo(() => {
    if (facet.type !== "range") {
      return {
        min: 0,
        max: 0,
        currentMin: 0,
        currentMax: 0,
        hasSelection: false,
      };
    }

    const { min, max, currentValue } = facet.data;

    return {
      min,
      max,
      currentMin: currentValue?.min ?? min,
      currentMax: currentValue?.max ?? max,
      hasSelection: !!currentValue,
    };
  }, [facet]);

  const [rangeValue, setRangeValue] = useState<RangeBoundaries>([
    bounds.currentMin,
    bounds.currentMax,
  ]);

  useEffect(() => {
    setRangeValue([bounds.currentMin, bounds.currentMax]);
  }, [bounds.currentMin, bounds.currentMax]);

  const handleChange = useCallback(
    ([rangeMin, rangeMax]: number[]) =>
      startTransition(() =>
        setRangeValue([rangeMin, rangeMax] as RangeBoundaries)
      ),
    [startTransition]
  );

  const handleRefine = useCallback(
    ([rangeMin, rangeMax]: RangeBoundaries) => {
      if (rangeMin === bounds.min && rangeMax === bounds.max) {
        clearKey(facet.key);
      } else {
        setRange(facet.key, rangeMin, rangeMax);
      }
    },
    [bounds.min, bounds.max, facet.key, setRange, clearKey]
  );

  const handleClear = useCallback(() => {
    clearKey(facet.key);
  }, [facet.key, clearKey]);

  if (facet.type !== "range") {
    return null;
  }

  return (
    <MobileFilterContainer className={classNames({ ["open"]: isOpen })}>
      <LabelNavigation
        label={title}
        leftAdornment={
          <MobileClearAllButton className="no-padding" onClick={toggleOpen}>
            <ArrowLeft strokeWidth="2" />
          </MobileClearAllButton>
        }
        rightAdornment={
          bounds.hasSelection ? (
            <MobileClearAllButton onClick={handleClear}>
              <Trans>Clear all</Trans>
            </MobileClearAllButton>
          ) : undefined
        }
      />
      <div className="content-wrapper">
        <Flex my={2} px={2} justifyContent="space-between">
          <Text fontWeight={600}>
            <Price price={Math.floor(rangeValue[0])} />
          </Text>
          <Text fontWeight={600}>
            <Price price={Math.ceil(rangeValue[1])} />
          </Text>
        </Flex>

        <Flex px={2}>
          <Range
            trackStyle={[
              { backgroundColor: theme.colors.tone.black },
              { backgroundColor: theme.colors.tone.marble },
            ]}
            handleStyle={[
              {
                backgroundColor: theme.colors.tone.white,
                border: `2px solid ${theme.colors.tone.black}`,
                height: "22px",
                width: "22px",
                top: "1px",
                boxShadow: "none",
              },
              {
                backgroundColor: theme.colors.tone.white,
                border: `2px solid ${theme.colors.tone.black}`,
                height: "22px",
                width: "22px",
                top: "1px",
                boxShadow: "none",
              },
            ]}
            railStyle={{ backgroundColor: theme.colors.tone.marble }}
            onChange={handleChange}
            onAfterChange={(value) => handleRefine(value as RangeBoundaries)}
            defaultValue={[bounds.min, bounds.max]}
            allowCross={false}
            min={bounds.min}
            max={bounds.max}
            value={rangeValue}
          />
        </Flex>

        <Button
          fullWidth
          variant="primary"
          onClick={toggleOpen}
          disabled={total <= 0}
          className="total-button"
        >
          <span>
            {total > 0 ? (
              <Trans>Show {total} results</Trans>
            ) : (
              <Trans>Close</Trans>
            )}
          </span>
        </Button>
      </div>
    </MobileFilterContainer>
  );
}

export { MobileRangeFilter };
